import { Navigate, createBrowserRouter } from 'react-router-dom';
import { Layout } from 'views/components/pages/layout';
import {
  Client,
  CombinedError,
  Exchange,
  Operation,
  Provider,
  cacheExchange,
  createClient,
  fetchExchange,
  mapExchange,
} from 'urql';
import { AuthConfig, AuthUtilities, authExchange } from '@urql/exchange-auth';
import { CustomerPagePath } from 'views/constants/enums/pageTitle';

// 顧客画面用Import
import { Login } from 'views/components/pages/login';
import { Home } from 'views/components/pages/home';
import { ProductList } from 'views/components/pages/productList';
import { ProductDetail } from 'views/components/pages/productDetail';
import { OrderStatus } from 'views/components/pages/orderStatus';
import { Notice } from 'views/components/pages/notice';
import { AccountInfo } from 'views/components/pages/accountInfo';
import { Contact } from 'views/components/pages/contact';
import { SendSMS } from 'views/components/pages/sendSMS';
import { ReissuePassword } from 'views/components/pages/reissuePassword';
import { Maintenance } from 'views/components/pages/maintenance';
import { TransferDistination } from 'views/components/pages/transferDistination';
import { AccountAsset } from 'views/components/pages/accountAsset';
//口座開設
import {
  AdvanceQuestion,
  CustomerInfo,
  Employment,
  ConsentSecurities,
  ConsentTrust,
  DepositAccount,
  InvestmentInfo,
  RequestConfirm,
  RequestComplete,
} from 'views/components/pages/accountEstablishment';
import { GraphQLError } from 'graphql';
import { useGlobalToastHandler } from 'views/components/organisms/globalToast/hooks';
import { ErrorPage } from 'views/components/pages/errorPage';
import { MFARedirect } from 'views/components/pages/mfaRedirect';
import { SecuritySetting } from 'views/components/pages/securitySetting';
import { AccountTransactionHistory } from 'views/components/pages/accountTransactionHistory';

export type CustomerMainRouterProps = {
  path: CustomerPagePath;
};

const isAuthError = (error: CombinedError) => {
  if (error.graphQLErrors.some((e: GraphQLError) => e.originalError?.message === '認証エラー')) {
    return true;
  } else if (error.response?.status === 401) {
    // あえて設定する必要があるがtokenを空にすると401が返ってくる
    return true;
  } else {
    return false;
  }
};

const createUrqlClient = (showErrorMessage: (message: string, durationMs?: number) => void) =>
  createClient({
    url: process.env.REACT_APP_API_URL!,
    exchanges: [
      cacheExchange,
      mapExchange({
        onError(error) {
          // 認証エラーの場合は何もしない 先にauthExchangeが呼ばれるが念のため
          if (isAuthError(error)) {
            return;
          }

          // API全体で共通で他の操作した場合にも消えないため時間指定で消す
          if (!error.response) {
            showErrorMessage('通信エラーが発生しました。通信環境を確認して再度お試しください。', 4000);
          } else if (error.response.status !== 200) {
            // APIサーバに到達できない場合などで200以外の場合を想定
            showErrorMessage('エラーが発生しました。しばらくしてから再度お試しください。', 4000);
          } else if (error.graphQLErrors) {
            // 型エラーなどバグレベルの場合
            showErrorMessage('エラーが発生しました。もう一度お試し頂くか、お問い合わせください。', 4000);
          }
        },
      }),
      authExchange(async (utils: AuthUtilities) => {
        return {
          addAuthToOperation(operation: Operation) {
            return operation;
          },
          didAuthError(error: CombinedError, _) {
            return isAuthError(error);
          },
          async refreshAuth() {
            sessionStorage.removeItem('token');
            window.location.href = CustomerPagePath.Login;
          },
        } as AuthConfig;
      }) as Exchange,
      fetchExchange,
    ],
    fetchOptions: () => {
      const token = sessionStorage.getItem('token');
      return {
        headers: {
          authorization: token ?? '-',
        },
      };
    },
  });

let client: Client;
const Root = () => {
  const { showError } = useGlobalToastHandler();
  client = client ?? createUrqlClient(showError);

  return (
    <Provider value={client}>
      <Layout />
    </Provider>
  );
};

// ログイン済みならHOME 未ログインならログイン画面に遷移
const SwitchTop = () => {
  const token = sessionStorage.getItem('token');

  // tokenがあればログイン済みとする 遷移先でAPIが呼ばれてtokenが不正ならログイン画面に戻る
  if (token) {
    return <Navigate to={CustomerPagePath.Home} />;
  } else {
    return <Navigate to={CustomerPagePath.Login} />;
  }
};

// TODO:CustomerPagePathから文字列を渡す
export const router = createBrowserRouter([
  {
    path: CustomerPagePath.Root,
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: CustomerPagePath.Root,
        element: <SwitchTop />,
      },
      {
        // ホーム
        path: CustomerPagePath.Home,
        element: <Home />,
      },
      {
        // 募集銘柄一覧
        path: CustomerPagePath.ProductList,
        element: <ProductList />,
      },
      {
        // 銘柄詳細
        path: `${CustomerPagePath.ProductDetail}/:symbol`,
        element: <ProductDetail />,
      },
      {
        // 申込履歴
        path: CustomerPagePath.OrderStatus,
        element: <OrderStatus />,
      },
      {
        // 資産管理
        path: CustomerPagePath.AccountAsset,
        element: <AccountAsset />,
      },
      {
        // 入出金履歴
        path: CustomerPagePath.AccountTransactionHistory,
        element: <AccountTransactionHistory />,
      },
      {
        // お知らせ
        path: CustomerPagePath.Notice,
        element: <Notice />,
      },
      {
        // 登録情報
        path: CustomerPagePath.AccountInfo,
        element: <AccountInfo />,
      },
      {
        // 振込先口座情報
        path: CustomerPagePath.TransferDistination,
        element: <TransferDistination />,
      },
      {
        // パスワードパスキー設定
        path: CustomerPagePath.SecuritySetting,
        element: <SecuritySetting />,
      },
      {
        // お問い合わせ
        path: CustomerPagePath.Contact,
        element: <Contact />,
      },
      {
        // ログイン
        path: CustomerPagePath.Login,
        element: <Login />,
      },
      {
        // SMS認証
        path: CustomerPagePath.SendSMS,
        element: <SendSMS />,
      },
      {
        // パスワード再発行
        path: CustomerPagePath.ReissuePassword,
        element: <ReissuePassword />,
      },
      {
        path: CustomerPagePath.Maintenance,
        element: <Maintenance />,
      },
      {
        // 口座開設_事前質問
        path: CustomerPagePath.AddvanceQuestion,
        element: <AdvanceQuestion />,
      },
      {
        // 口座開設_証券同意
        path: CustomerPagePath.ConsentSecurities,
        element: <ConsentSecurities />,
      },
      {
        // 口座開設_信託同意
        path: CustomerPagePath.ConsentTrust,
        element: <ConsentTrust />,
      },
      {
        // 口座開設_お客様情報
        path: CustomerPagePath.CustomerInfo,
        element: <CustomerInfo />,
      },
      {
        // 口座開設_指定預金口座情報
        path: CustomerPagePath.DepositAccount,
        element: <DepositAccount />,
      },
      {
        // 口座開設_お勤め先情報
        path: CustomerPagePath.Employment,
        element: <Employment />,
      },
      {
        // 口座開設_投資情報
        path: CustomerPagePath.InvestmentInfo,
        element: <InvestmentInfo />,
      },
      {
        // 口座開設_入力確認
        path: CustomerPagePath.RequestConfirm,
        element: <RequestConfirm />,
      },
      {
        // 口座開設_口座開設完了
        path: CustomerPagePath.RequestComplete,
        element: <RequestComplete />,
      },
      {
        // MFAのユーザ検証後のリダイレクト
        path: CustomerPagePath.MFARedirect,
        element: <MFARedirect />,
      },
    ],
  },
]);
